// src/components/modules/DealDesk.js
// Deal Desk inside the Timesheet app (route /deal-desk).
//
// The Deal Desk page itself (public/deal-desk/index.html) keeps its exact design and features. It runs in a
// same-origin iframe; public/deal-desk/claude-shim.js gives it our API through the bridge set up here:
//   api(method, url, { params, body, signal })  → Timesheetapi (the app's sign-in), adds empid
//   graphToken()                                → Microsoft Graph token (Mail.Read) for Outlook search, via MSAL
//   config                                      lists / prices / catalog from api/DealDesk/Config (DealDesk_Config.sql tables)
// Data is saved in SQL Server by Controllers/DealDeskController.cs. Access: dbo.DealDesk_Access.

import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Timesheetapi } from "../../middleware/interceptor";

const GRAPH_SCOPES = ["Mail.Read"];

const who = () => ({ empid: Number(localStorage.getItem("employee_id")) || undefined });

// Sidebar / page: { access, admin, ai, fireflies, ... } — never throws
export async function fetchDealDeskAccess() {
  try {
    const res = await Timesheetapi.get("api/DealDesk/Me", { params: who() });
    return res?.data?.status === 1 ? res.data : { access: false };
  } catch {
    return { access: false };
  }
}

// view: "deals" (sidebar → Deal Desk, /deal-desk) or "admin" (sidebar → Settings → Deal Desk Admin, /deal-desk/admin)
export const DealDesk = ({ view = "deals" }) => {
  const { instance, accounts } = useMsal();
  const [state, setState] = useState("loading"); // loading | ready | denied | notadmin
  const configRef = useRef(null);                  // api/DealDesk/Config, read by the page when it loads

  // Bridge for the iframe (must exist before the iframe loads)
  useEffect(() => {
    window.__dealDeskBridge = {
      get config() { return configRef.current; },
      api: async (method, url, { params, body, signal } = {}) => {
        const res = await Timesheetapi.request({
          method,
          url,
          params: { ...who(), ...(params || {}) },
          data: body ? { ...who(), ...body } : undefined,
          signal,
        });
        return res.data;
      },
      graphToken: async () => {
        const account = instance.getActiveAccount() || accounts[0];
        if (!account) return null;
        try {
          return (await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account })).accessToken;
        } catch (err) {
          if (err instanceof InteractionRequiredAuthError) {
            return (await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES, account })).accessToken;
          }
          throw err;
        }
      },
    };
    return () => { delete window.__dealDeskBridge; };
  }, [instance, accounts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await fetchDealDeskAccess();
      if (!me.access) { if (!cancelled) setState("denied"); return; }
      if (view === "admin" && !me.admin) { if (!cancelled) setState("notadmin"); return; }
      try {
        const res = await Timesheetapi.get("api/DealDesk/Config", { params: who() });
        configRef.current = res?.data?.status === 1 ? res.data.config : null;
      } catch (err) {
        console.error("Deal Desk config load failed (using built-in lists):", err);
        configRef.current = null;
      }
      if (!cancelled) setState("ready");
    })();
    return () => { cancelled = true; };
  }, [view]);

  if (state === "loading") {
    return <div className="p-8"><div className="h-10 w-64 rounded-lg bg-[#F1F3F7] animate-pulse" /></div>;
  }
  if (state === "notadmin") {
    return (
      <div className="p-8 max-w-[640px]">
        <h1 className="m-0 text-[22px] font-semibold text-[#0F172A]">Deal Desk Admin</h1>
        <p className="text-[14px] text-[#5B6474]">Only Deal Desk admins can change these settings.</p>
      </div>
    );
  }
  if (state === "denied") {
    return (
      <div className="p-8 max-w-[640px]">
        <h1 className="m-0 text-[22px] font-semibold text-[#0F172A]">Deal Desk</h1>
        <p className="text-[14px] text-[#5B6474]">You don't have access to Deal Desk. Ask a Deal Desk admin to add you.</p>
      </div>
    );
  }
  // TOP_GAP keeps the app's "Hi, <name>" chip (top-right) from covering Deal Desk's own menu (Deals · How to use · Admin)
  const TOP_GAP = 36;   // was 64: just enough to clear the chip (Deal Desk's own top bar is hidden)
  return (
    <div style={{ paddingTop: TOP_GAP }}>
      <iframe
        key={view}                                   /* switching Deals ↔ Admin reloads the page on the right view */
        title={view === "admin" ? "Deal Desk Admin" : "Deal Desk"}
        src={view === "admin" ? "/deal-desk/index.html#admin" : "/deal-desk/index.html#leads"}
        className="block w-full border-0 bg-white"
        style={{ height: `calc(100vh - ${TOP_GAP}px)` }}
      />
    </div>
  );
};

export default DealDesk;